/**
 * @fileoverview 資格・期限管理ページ（/crew-certificates）
 * @description シーステージ様の運用Excel「各人有効期限」「船別 免状有効期限表」をシステム化したもの。
 *              - 一覧: 船ごとにグループした「乗組員 × 資格種別」のマトリクス。残日数で色分け、年齢は定年の見張り
 *              - 期限順: 期限切れ・警告・注意・更新手続き中・未登録を残日数順に並べたワークリスト
 *              - 種別・設定: 資格種別マスタ（有効期間・警告日数・通知段階・本人/船長への表示）と定年設定
 *              - Excel出力: 全社一覧（各人有効期限）／船別 有効期限表（令和表記・印刷向き）
 *              行をクリックすると人材カード（労務・書類タブ）が開き、そこで追加・更新登録ができる。
 *              機能フラグ crew_certificates の会社のみ（サイドバーもフラグで出し分け）。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Award, AlertTriangle, Download, ChevronDown, RefreshCw, Search, Ship, Settings2, Plus, Pencil, Trash2,
  ArrowUp, ArrowDown, Bell, Clock, UserX, CalendarClock, X, ListOrdered, LayoutGrid, Info,
} from 'lucide-react';
import * as api from '../services/api';
import TalentDetailDrawer from '../components/TalentDetailDrawer';
import AvatarEditor from '../components/AvatarEditor';
import {
  CertLevelPill, LEVEL_STYLE, CATEGORY_LABEL, daysText, fmtYmd, downloadWithAuth,
  type CertLevel, type CertRecord, type CertTypeRow,
} from '../components/CrewCertificatePanel';

interface OverviewRow {
  id: string; employee_code: string; user_name: string; name_kana: string | null; avatar_path: string | null;
  birth_date: string | null; age: number | null; rank_position: string | null;
  vessel_id: string | null; vessel_name: string | null; vessel_kind: 'on_board' | 'scheduled' | 'last' | 'none';
  retire_at: string | null; retire_level: 'none' | 'ok' | 'warn' | 'reached';
  certs: CertRecord[]; missing_type_ids: string[]; worst_level: string;
}
interface Overview {
  today: string; types: CertTypeRow[]; vessels: { id: string; vessel_name: string }[]; rows: OverviewRow[];
  summary: { users: number; expired: number; danger: number; warn: number; renewing: number; missing_required: number; retire_warn: number; retire_reached: number };
  settings: { retirement_age: number; retirement_warn_years: number };
}
type PageTab = 'matrix' | 'worklist' | 'master';
type LevelFilter = '' | 'expired' | 'danger' | 'warn' | 'renewing' | 'missing' | 'retire';

const ymdStamp = () => { const t = new Date(); return `${t.getFullYear()}${String(t.getMonth() + 1).padStart(2, '0')}${String(t.getDate()).padStart(2, '0')}`; };
const errMsg = (e: unknown, fb: string) => { const m = (e as any)?.message ?? (e as any)?.error?.message; return typeof m === 'string' && m ? m : fb; };
const LEVEL_RANK: Record<string, number> = { expired: 6, danger: 5, warn: 4, missing: 3, renewing: 2, ok: 1, none: 0 };

const CrewCertificatesPage: React.FC = () => {
  const [ov, setOv] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<PageTab>('matrix');
  const [q, setQ] = useState('');
  const [vesselFilter, setVesselFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('');
  const [daysFilter, setDaysFilter] = useState<number>(0);   // 0 = 指定なし
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get<any>('/crew-certificates/overview'); setOv(r.data as Overview); }
    catch (e) { setToast(errMsg(e, '読み込みに失敗しました')); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); }, [toast]);

  const types = ov?.types ?? [];
  const rows = ov?.rows ?? [];

  // ── フィルタ ──
  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return rows.filter(r => {
      if (vesselFilter === '__none') { if (r.vessel_id) return false; }
      else if (vesselFilter && r.vessel_id !== vesselFilter) return false;
      if (kw && !(`${r.user_name} ${r.name_kana ?? ''} ${r.employee_code} ${r.rank_position ?? ''}`.toLowerCase().includes(kw))) return false;
      if (levelFilter === 'retire') return r.retire_level === 'warn' || r.retire_level === 'reached';
      if (levelFilter === 'missing') return r.missing_type_ids.length > 0;
      if (levelFilter) return r.certs.some(c => c.level === levelFilter);
      if (daysFilter > 0) return r.certs.some(c => c.days != null && c.days <= daysFilter) || r.missing_type_ids.length > 0;
      return true;
    });
  }, [rows, q, vesselFilter, levelFilter, daysFilter]);

  // 船ごとにグループ（乗船中 → 予定 → 直近の船。未配属は最後）
  const groups = useMemo(() => {
    const m = new Map<string, { name: string; rows: OverviewRow[] }>();
    for (const r of filtered) {
      const key = r.vessel_id ?? '__none';
      const g = m.get(key) ?? { name: r.vessel_name ?? '未配属・陸上', rows: [] };
      g.rows.push(r); m.set(key, g);
    }
    return [...m.entries()].sort(([a, ga], [b, gb]) => (a === '__none' ? 1 : 0) - (b === '__none' ? 1 : 0) || ga.name.localeCompare(gb.name, 'ja'))
      .map(([key, g]) => ({ key, name: g.name, rows: g.rows.sort((x, y) => (LEVEL_RANK[y.worst_level] ?? 0) - (LEVEL_RANK[x.worst_level] ?? 0) || x.user_name.localeCompare(y.user_name, 'ja')) }));
  }, [filtered]);

  // 期限順ワークリスト
  const worklist = useMemo(() => {
    const items: { key: string; row: OverviewRow; cert?: CertRecord; type?: CertTypeRow; level: CertLevel; days: number | null }[] = [];
    for (const r of filtered) {
      for (const c of r.certs) {
        if (!['expired', 'danger', 'warn', 'renewing'].includes(c.level)) continue;
        if (daysFilter > 0 && c.days != null && c.days > daysFilter && c.level !== 'expired') continue;
        items.push({ key: c.id, row: r, cert: c, level: c.level as CertLevel, days: c.days });
      }
      for (const tid of r.missing_type_ids) items.push({ key: `${r.id}-${tid}`, row: r, type: types.find(t => t.id === tid), level: 'missing', days: null });
    }
    return items.sort((a, b) => (a.level === 'missing' ? 1 : 0) - (b.level === 'missing' ? 1 : 0) || (a.days ?? 1e9) - (b.days ?? 1e9));
  }, [filtered, types, daysFilter]);

  const exportXlsx = async (format: 'company' | 'vessel', vesselId?: string) => {
    setShowExport(false);
    try {
      const v = vesselId ? ov?.vessels.find(x => x.id === vesselId)?.vessel_name : null;
      await downloadWithAuth(`/crew-certificates/export?format=${format}${vesselId ? `&vessel_id=${vesselId}` : ''}`,
        format === 'company' ? `各人有効期限_${ymdStamp()}.xlsx` : `${v ?? '船別'}_免状有効期限表_${ymdStamp()}.xlsx`);
    } catch { setToast('Excelの作成に失敗しました'); }
  };

  const runScan = async () => {
    setScanning(true);
    try { const r = await api.post<any>('/crew-certificates/scan', {}); setToast(`チェック完了：新しい通知 ${r.data?.alerts ?? 0} 件（本人通知 ${r.data?.notified ?? 0} 件）`); await load(true); }
    catch (e) { setToast(errMsg(e, 'チェックに失敗しました')); }
    setScanning(false);
  };

  const quickRenewing = async (c: CertRecord) => {
    try {
      const next = c.status === 'renewing' ? 'active' : 'renewing';
      const note = next === 'renewing' ? window.prompt('更新手続きのメモ（任意）。例: 講習を10/15に予約', c.renewal_note ?? '') : '';
      if (note === null) return;
      await api.put(`/crew-certificates/${c.id}`, { status: next, renewal_note: note });
      await load(true);
    } catch (e) { setToast(errMsg(e, '更新に失敗しました')); }
  };

  const selectStyle: React.CSSProperties = { padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', color: '#334155' };
  const s = ov?.summary;

  // ── サマリータイル ──
  const tiles: { key: LevelFilter; label: string; value: number; color: string; bg: string; Icon: React.FC<any>; hint: string }[] = s ? [
    { key: 'expired', label: '期限切れ', value: s.expired, color: '#dc2626', bg: '#fef2f2', Icon: AlertTriangle, hint: '今すぐ更新が必要' },
    { key: 'danger', label: '警告', value: s.danger, color: '#b91c1c', bg: '#fee2e2', Icon: Bell, hint: '種別ごとの警告日数以内' },
    { key: 'warn', label: '注意', value: s.warn, color: '#b45309', bg: '#fffbeb', Icon: Clock, hint: '種別ごとの注意日数以内' },
    { key: 'renewing', label: '更新手続き中', value: s.renewing, color: '#1d4ed8', bg: '#eff6ff', Icon: RefreshCw, hint: '申請・講習予約済み' },
    { key: 'missing', label: '未登録', value: s.missing_required, color: '#475569', bg: '#f1f5f9', Icon: UserX, hint: '必須の資格が登録されていない' },
    { key: 'retire', label: '定年間近', value: s.retire_warn + s.retire_reached, color: '#7c3aed', bg: '#f5f3ff', Icon: CalendarClock, hint: `定年${ov?.settings.retirement_age}歳の${ov?.settings.retirement_warn_years}年前から` },
  ] : [];

  const ageCell = (r: OverviewRow) => {
    if (r.age == null) return <span style={{ color: '#cbd5e1' }}>-</span>;
    const st = r.retire_level === 'reached' ? { bg: '#dc2626', fg: '#fff' } : r.retire_level === 'warn' ? { bg: '#fef3c7', fg: '#b45309' } : null;
    return (
      <span title={r.retire_at ? `定年到達日 ${fmtYmd(r.retire_at)}（${ov?.settings.retirement_age}歳）` : undefined}
        style={{ display: 'inline-block', minWidth: 30, textAlign: 'center', padding: '1px 6px', borderRadius: 6, fontWeight: st ? 800 : 500, background: st?.bg, color: st?.fg ?? '#334155', fontSize: 12.5 }}>
        {r.age}
      </span>
    );
  };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Award size={20} color="#0369a1" />資格・期限管理</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            海技免状・船員手帳・STCW基本訓練・健康証明書などの有効期限を船別に一覧し、更新漏れを防ぎます。{ov && <span style={{ color: '#94a3b8' }}>　{fmtYmd(ov.today)} 現在</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn-o" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={runScan} disabled={scanning} title="毎日自動で実行されます。今すぐ通知を出したいときに使います">
            <RefreshCw size={14} className={scanning ? 'spin' : undefined} />{scanning ? 'チェック中…' : '今すぐチェック'}
          </button>
          <div style={{ position: 'relative' }}>
            <button className="btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setShowExport(v => !v)}><Download size={14} />Excel出力 <ChevronDown size={13} /></button>
            {showExport && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 300, overflow: 'hidden' }}>
                {[
                  { label: '各人有効期限（全社一覧）', desc: '船別に全員・全種別。2枚目に期限順リスト', run: () => exportXlsx('company') },
                  { label: '船別 有効期限表（全船・船ごとに1シート）', desc: '船に貼り出す印刷向き。令和表記の「現在」日付つき', run: () => exportXlsx('vessel') },
                  ...(vesselFilter && vesselFilter !== '__none' ? [{ label: `船別 有効期限表（${ov?.vessels.find(v => v.id === vesselFilter)?.vessel_name ?? ''}のみ）`, desc: '絞り込み中の船だけを1シートで', run: () => exportXlsx('vessel', vesselFilter) }] : []),
                ].map(it => (
                  <button key={it.label} onClick={it.run} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', background: '#fff', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')} onMouseLeave={e => (e.currentTarget.style.background = '#fff')}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{it.label}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{it.desc}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* サマリー（クリックで絞り込み） */}
      {s && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
          {tiles.map(t => {
            const active = levelFilter === t.key;
            return (
              <button key={t.key} onClick={() => { setLevelFilter(active ? '' : t.key); setDaysFilter(0); if (tab === 'master') setTab('matrix'); }} title={t.hint}
                style={{ textAlign: 'left', background: active ? t.bg : '#fff', border: `1.5px solid ${active ? t.color : '#e5e7eb'}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><t.Icon size={17} color={t.color} /></span>
                <span>
                  <span style={{ display: 'block', fontSize: 22, fontWeight: 800, color: t.value > 0 ? t.color : '#94a3b8', lineHeight: 1.1 }}>{t.value}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{t.label}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* タブ＋フィルタ */}
      <div className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3 }}>
          {([['matrix', '一覧（船別）', LayoutGrid], ['worklist', '期限順リスト', ListOrdered], ['master', '種別・設定', Settings2]] as [PageTab, string, React.FC<any>][]).map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, background: tab === k ? '#fff' : 'transparent', color: tab === k ? '#0369a1' : '#64748b', boxShadow: tab === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
              <I size={13} />{l}
            </button>
          ))}
        </div>
        {tab !== 'master' && (
          <>
            <div style={{ position: 'relative' }}>
              <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 10 }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="氏名・社員コード・役職" style={{ ...selectStyle, paddingLeft: 30, width: 200, cursor: 'text' }} />
            </div>
            <select style={selectStyle} value={vesselFilter} onChange={e => setVesselFilter(e.target.value)}>
              <option value="">すべての船</option>
              {ov?.vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
              <option value="__none">未配属・陸上</option>
            </select>
            <select style={selectStyle} value={daysFilter} onChange={e => { setDaysFilter(Number(e.target.value)); setLevelFilter(''); }}>
              <option value={0}>残日数: 指定なし</option>
              {[30, 60, 90, 180, 365].map(d => <option key={d} value={d}>{d}日以内（＋期限切れ）</option>)}
            </select>
            {(levelFilter || daysFilter || vesselFilter || q) && (
              <button className="btn-o" style={{ padding: '6px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => { setLevelFilter(''); setDaysFilter(0); setVesselFilter(''); setQ(''); }}><X size={12} />解除</button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>{filtered.length} / {rows.length} 名</span>
          </>
        )}
      </div>

      {loading && <div className="empty">読み込み中…</div>}

      {/* ═══ 一覧（マトリクス） ═══ */}
      {!loading && ov && tab === 'matrix' && (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ minWidth: 720 + types.length * 150 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2, minWidth: 220 }}>乗組員</th>
                <th style={{ width: 56, textAlign: 'center' }}>年齢</th>
                {types.map(t => (
                  <th key={t.id} style={{ minWidth: 150 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: t.color, flexShrink: 0 }} />
                      <span>{t.short_name ?? t.name}</span>
                      {!!t.required && <span title="全乗組員に必要" style={{ fontSize: 9.5, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '0 4px' }}>必須</span>}
                    </span>
                  </th>
                ))}
                <th style={{ minWidth: 180 }}>備考</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.key}>
                  <tr>
                    <td colSpan={3 + types.length} style={{ background: '#eef4fb', padding: '6px 14px', fontWeight: 800, color: '#0f2d54', fontSize: 12.5 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Ship size={13} />{g.name}<span style={{ color: '#64748b', fontWeight: 500 }}>　{g.rows.length}名</span></span>
                    </td>
                  </tr>
                  {g.rows.map(r => {
                    const remarks: string[] = [];
                    for (const c of r.certs) { if (c.status === 'renewing') remarks.push(`${c.type_name.replace(/（.*?）/g, '')}: 手続き中${c.renewal_note ? `（${c.renewal_note}）` : ''}`); if (c.attention && c.note) remarks.push(c.note); }
                    return (
                      <tr key={r.id} onClick={() => setSelectedId(r.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div onClick={e => e.stopPropagation()}><AvatarEditor entityId={r.id} type="user" imagePath={r.avatar_path} displayName={r.user_name} size={32} readOnly onUpdated={() => {}} /></div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                {r.user_name}
                                {r.vessel_kind === 'scheduled' && <span style={{ fontSize: 10, color: '#2563eb', background: '#eff6ff', borderRadius: 4, padding: '0 5px' }}>乗船予定</span>}
                                {r.vessel_kind === 'last' && <span style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '0 5px' }}>陸上</span>}
                              </div>
                              <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{[r.rank_position, r.employee_code].filter(Boolean).join(' ・ ')}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>{ageCell(r)}</td>
                        {types.map(t => {
                          const mine = r.certs.filter(c => c.type_id === t.id);
                          if (mine.length === 0) {
                            return <td key={t.id}>{t.required ? <CertLevelPill level="missing" compact /> : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}</td>;
                          }
                          return (
                            <td key={t.id}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {mine.map(c => (
                                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <CertLevelPill level={c.level} days={c.days} date={c.expiry_date} compact title={`${c.type_name}${c.grade ? ' ' + c.grade : ''}${c.cert_no ? ' ' + c.cert_no : ''}`} />
                                    {!!c.attention && <AlertTriangle size={12} color="#b45309" />}
                                  </div>
                                ))}
                                {mine.some(c => c.grade) && <div style={{ fontSize: 10.5, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170 }}>{mine.map(c => c.grade).filter(Boolean).join(' / ')}</div>}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ maxWidth: 240 }}>
                          {remarks.length > 0
                            ? <span title={remarks.join('\n')} style={{ fontSize: 11.5, color: '#78350f', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{remarks.join(' / ')}</span>
                            : <span style={{ color: '#cbd5e1' }}>-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {groups.length === 0 && <tr><td colSpan={3 + types.length} className="empty">該当する乗組員がいません</td></tr>}
            </tbody>
          </table>
          <div style={{ padding: '8px 14px', fontSize: 11, color: '#94a3b8', display: 'flex', gap: 14, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9' }}>
            {(['expired', 'danger', 'warn', 'renewing', 'ok', 'missing'] as CertLevel[]).map(l => <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: LEVEL_STYLE[l].bg, border: `1px solid ${LEVEL_STYLE[l].border}` }} />{LEVEL_STYLE[l].label}</span>)}
            <span style={{ marginLeft: 'auto' }}>行をクリックすると人材カードが開き、追加・更新登録・スキャン添付ができます。閾値は「種別・設定」で変更できます。</span>
          </div>
        </div>
      )}

      {/* ═══ 期限順リスト ═══ */}
      {!loading && ov && tab === 'worklist' && (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table>
            <thead><tr><th style={{ width: 90 }}>残日数</th><th>状態</th><th>船</th><th>乗組員</th><th>種別</th><th>種類・番号</th><th>有効期限</th><th>更新状況・留意事項</th><th style={{ width: 150 }}></th></tr></thead>
            <tbody>
              {worklist.map(it => {
                const st = LEVEL_STYLE[it.level];
                return (
                  <tr key={it.key} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(it.row.id)}>
                    <td style={{ whiteSpace: 'nowrap' }}><span style={{ fontWeight: 800, color: st.fg === '#ffffff' ? '#dc2626' : st.fg, fontSize: 13 }}>{daysText(it.days, it.level)}</span></td>
                    <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: st.bg, color: st.fg, border: `1px solid ${st.border}`, whiteSpace: 'nowrap' }}>{st.label}</span></td>
                    <td style={{ fontSize: 12.5 }}>{it.row.vessel_name ?? <span style={{ color: '#94a3b8' }}>未配属</span>}</td>
                    <td><div style={{ fontWeight: 700 }}>{it.row.user_name}</div><div style={{ fontSize: 10.5, color: '#94a3b8' }}>{it.row.rank_position ?? ''}</div></td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: it.cert?.type_color ?? it.type?.color ?? '#64748b' }} />{it.cert?.type_name ?? it.type?.name}</span></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{[it.cert?.grade, it.cert?.cert_no].filter(Boolean).join(' / ') || '-'}</td>
                    <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{it.cert ? fmtYmd(it.cert.expiry_date) : '-'}</td>
                    <td style={{ fontSize: 12, color: '#78350f', maxWidth: 260 }}>
                      {it.cert?.status === 'renewing' && <span style={{ color: '#1d4ed8' }}>更新手続き中{it.cert.renewal_note ? `（${it.cert.renewal_note}）` : ''} </span>}
                      {!!it.cert?.attention && <span><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> {it.cert.note}</span>}
                      {it.level === 'missing' && <span style={{ color: '#64748b' }}>必須の資格が未登録</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {it.cert && (
                        <button className="btn-o" style={{ padding: '4px 9px', fontSize: 11, marginRight: 4 }} onClick={() => quickRenewing(it.cert!)}>{it.cert.status === 'renewing' ? '手続き中を解除' : '手続き中にする'}</button>
                      )}
                      <button className="btn-o" style={{ padding: '4px 9px', fontSize: 11 }} onClick={() => setSelectedId(it.row.id)}>{it.level === 'missing' ? '登録' : '更新登録'}</button>
                    </td>
                  </tr>
                );
              })}
              {worklist.length === 0 && <tr><td colSpan={9} className="empty">対象の資格はありません。すべて期限内です。</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ 種別・設定 ═══ */}
      {!loading && ov && tab === 'master' && <MasterTab ov={ov} onChanged={() => load(true)} setToast={setToast} />}

      {selectedId && (
        <TalentDetailDrawer userId={selectedId} initialTab="labor" onClose={() => { setSelectedId(null); load(true); }} onChanged={() => load(true)} />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, background: '#0f172a', color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>{toast}</div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .spin{animation:spin 1s linear infinite}`}</style>
    </div>
  );
};

// ── 種別マスタ＋定年設定 ─────────────────────────────────────────────────
const MasterTab: React.FC<{ ov: Overview; onChanged: () => void; setToast: (m: string) => void }> = ({ ov, onChanged, setToast }) => {
  const [types, setTypes] = useState<CertTypeRow[]>([]);
  const [editing, setEditing] = useState<Partial<CertTypeRow> | null>(null);
  const [saving, setSaving] = useState(false);
  const [ret, setRet] = useState({ retirement_age: ov.settings.retirement_age, retirement_warn_years: ov.settings.retirement_warn_years });
  const [retDirty, setRetDirty] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get<any>('/crew-certificates/types', { all: '1' }); setTypes((r.data ?? []) as CertTypeRow[]); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveType = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id) await api.put(`/crew-certificates/types/${editing.id}`, editing);
      else await api.post('/crew-certificates/types', editing);
      setEditing(null); await load(); onChanged();
    } catch (e) { setToast(errMsg(e, '保存に失敗しました')); }
    setSaving(false);
  };
  const removeType = async (t: CertTypeRow) => {
    if (!window.confirm(`「${t.name}」を削除しますか？登録済みの資格がある場合は削除せず無効になります。`)) return;
    try { const r = await api.del<any>(`/crew-certificates/types/${t.id}`); if (r.data?.message) setToast(r.data.message); await load(); onChanged(); }
    catch (e) { setToast(errMsg(e, '削除に失敗しました')); }
  };
  const move = async (idx: number, dir: -1 | 1) => {
    const arr = [...types]; const j = idx + dir; if (j < 0 || j >= arr.length) return;
    const a = arr[idx]!, b = arr[j]!; arr[idx] = b; arr[j] = a;
    setTypes(arr);
    try { await api.put('/crew-certificates/types/reorder', { ids: arr.map(t => t.id) }); onChanged(); } catch { /* noop */ }
  };
  const saveRet = async () => {
    try { await api.put('/crew-certificates/settings', ret); setRetDirty(false); setToast('定年の設定を保存しました'); onChanged(); }
    catch (e) { setToast(errMsg(e, '保存に失敗しました')); }
  };

  const input: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: '#fff' };
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, display: 'block' };
  const Toggle: React.FC<{ k: keyof CertTypeRow; label: string; hint?: string }> = ({ k, label: l, hint }) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#334155', cursor: 'pointer' }}>
      <input type="checkbox" checked={!!(editing as any)?.[k]} onChange={e => setEditing(p => ({ ...p, [k]: e.target.checked ? 1 : 0 }))} style={{ marginTop: 3 }} />
      <span><b>{l}</b>{hint && <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>{hint}</span>}</span>
    </label>
  );
  const monthsLabel = (m: number | null) => !m ? '手入力' : m % 12 === 0 ? `${m / 12}年` : `${m}ヶ月`;

  return (
    <>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 14 }}>資格種別マスタ</div>
            <div style={{ fontSize: 11.5, color: '#64748b' }}>有効期間・警告日数・通知の段階・本人や船長に見せるかを種別ごとに決めます。無線従事者免許・危険物・雇入契約期間などは「追加」で増やせます。</div>
          </div>
          <button className="btn-p" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', fontSize: 12.5 }}
            onClick={() => setEditing({ name: '', short_name: '', category: 'other', validity_months: 12, warn_days: 180, danger_days: 60, notify_stages: '180,90,30,0', notify_admin: 1, notify_self: 1, visible_to_self: 1, visible_to_captain: 1, required: 0, allow_multiple: 0, has_number: 1, has_grade: 0, color: '#0369a1', description: '', is_active: 1 })}>
            <Plus size={13} />種別を追加
          </button>
        </div>
        <table>
          <thead><tr><th style={{ width: 60 }}>順</th><th>種別</th><th>分類</th><th>有効期間</th><th>注意 / 警告</th><th>通知段階（残日数）</th><th>通知先</th><th>表示</th><th>必須</th><th style={{ width: 110 }}></th></tr></thead>
          <tbody>
            {types.map((t, i) => (
              <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.5 }}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button onClick={() => move(i, -1)} disabled={i === 0} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}><ArrowUp size={13} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === types.length - 1} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}><ArrowDown size={13} /></button>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color }} />
                    <div>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{t.name}{t.short_name && <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 11 }}>（{t.short_name}）</span>}{!t.is_active && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>無効</span>}</div>
                      {t.description && <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 360 }}>{t.description}</div>}
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12 }}>{CATEGORY_LABEL[t.category] ?? t.category}</td>
                <td style={{ fontSize: 12.5 }}>{monthsLabel(t.validity_months)}</td>
                <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}><span style={{ color: '#b45309' }}>{t.warn_days}日</span> / <span style={{ color: '#b91c1c' }}>{t.danger_days}日</span></td>
                <td style={{ fontSize: 12.5 }}>{t.notify_stages.split(',').map(x => x.trim()).filter(Boolean).map(x => x === '0' ? '当日' : `${x}日前`).join('・')}</td>
                <td style={{ fontSize: 11.5, color: '#64748b' }}>{[t.notify_admin ? '陸上' : '', t.notify_self ? '本人' : ''].filter(Boolean).join('＋') || 'なし'}</td>
                <td style={{ fontSize: 11.5, color: '#64748b' }}>{[t.visible_to_self ? '本人' : '', t.visible_to_captain ? '船長' : ''].filter(Boolean).join('・') || '管理者のみ'}</td>
                <td style={{ fontSize: 12 }}>{t.required ? <span style={{ color: '#b45309', fontWeight: 700 }}>必須</span> : '-'}{!!t.allow_multiple && <div style={{ fontSize: 10.5, color: '#94a3b8' }}>複数可</div>}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button className="btn-o" style={{ padding: '4px 9px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setEditing({ ...t })}><Pencil size={11} />編集</button>
                  {!t.is_system && <button className="btn-o" style={{ padding: '4px 7px', fontSize: 11, marginLeft: 4, color: '#dc2626' }} onClick={() => removeType(t)}><Trash2 size={11} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '8px 16px', fontSize: 11, color: '#94a3b8', display: 'flex', gap: 6, alignItems: 'flex-start', borderTop: '1px solid #f1f5f9' }}>
          <Info size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>「注意」「警告」の日数は一覧の色分け、「通知段階」は毎日の自動チェックで陸上管理者（アラート）と本人（連絡・お知らせ＋プッシュ）に通知を出す残日数です。期限切れは別に1回通知します。「更新手続き中」にした資格は通知を止めます。重大度は登録マスタ → アラート設定（海技免状の期限／船員手帳の期限／その他資格の期限）で変えられます。</span>
        </div>
      </div>

      {/* 定年 */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}><CalendarClock size={15} color="#7c3aed" />定年の見張り</div>
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>一覧の年齢を、定年の指定年数前から黄色、到達で赤にします（Excelの63・64・65歳の色分けに相当）。再雇用の扱いは各社の規程に合わせて年齢を変えてください。</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div><label style={label}>定年年齢</label><input type="number" min={40} max={90} style={{ ...input, width: 90 }} value={ret.retirement_age} onChange={e => { setRet(p => ({ ...p, retirement_age: Number(e.target.value) })); setRetDirty(true); }} /></div>
            <div><label style={label}>何年前から注意</label><input type="number" min={0} max={10} style={{ ...input, width: 90 }} value={ret.retirement_warn_years} onChange={e => { setRet(p => ({ ...p, retirement_warn_years: Number(e.target.value) })); setRetDirty(true); }} /></div>
            <button className="btn-p" disabled={!retDirty} onClick={saveRet} style={{ padding: '8px 14px' }}>保存</button>
          </div>
        </div>
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEditing(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(680px, 100%)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>{editing.id ? '種別を編集' : '種別を追加'}</div>
              {editing.is_system ? <span style={{ fontSize: 10.5, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>標準（免状・手帳との連携に使うため削除不可）</span> : null}
              <button onClick={() => setEditing(null)} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b' }}><X size={18} /></button>
            </div>
            <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>
              <div><label style={label}>名称</label><input style={input} value={editing.name ?? ''} onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} placeholder="例: 無線従事者免許" /></div>
              <div><label style={label}>短い名前（一覧の列見出し）</label><input style={input} value={editing.short_name ?? ''} onChange={e => setEditing(p => ({ ...p, short_name: e.target.value }))} placeholder="例: 無線" /></div>
              <div>
                <label style={label}>分類</label>
                <select style={input} value={editing.category ?? 'other'} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))}>
                  {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label style={label}>色</label><input type="color" style={{ ...input, padding: 2, height: 36 }} value={editing.color ?? '#0369a1'} onChange={e => setEditing(p => ({ ...p, color: e.target.value }))} /></div>
              <div>
                <label style={label}>有効期間（月）<span style={{ color: '#94a3b8', fontWeight: 500 }}> 発行日から期限を自動計算。空欄＝手入力</span></label>
                <input type="number" min={0} style={input} value={editing.validity_months ?? ''} onChange={e => setEditing(p => ({ ...p, validity_months: e.target.value === '' ? null : Number(e.target.value) }))} placeholder="例: 60（5年）" />
              </div>
              <div>
                <label style={label}>通知段階（残日数・カンマ区切り。0＝当日）</label>
                <input style={input} value={editing.notify_stages ?? ''} onChange={e => setEditing(p => ({ ...p, notify_stages: e.target.value }))} placeholder="例: 180,90,30,0" />
              </div>
              <div><label style={label}>注意（黄）になる残日数</label><input type="number" min={0} style={input} value={editing.warn_days ?? 180} onChange={e => setEditing(p => ({ ...p, warn_days: Number(e.target.value) }))} /></div>
              <div><label style={label}>警告（赤）になる残日数</label><input type="number" min={0} style={input} value={editing.danger_days ?? 60} onChange={e => setEditing(p => ({ ...p, danger_days: Number(e.target.value) }))} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={label}>説明（入力画面に表示）</label><input style={input} value={editing.description ?? ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} /></div>
              <div style={{ display: 'grid', gap: 8 }}>
                <Toggle k="required" label="全乗組員に必須" hint="未登録の人を「未登録」として一覧・通知に出す" />
                <Toggle k="allow_multiple" label="1人が複数枚持てる" hint="海技免状の航海＋機関など" />
                <Toggle k="has_grade" label="種類・等級の欄を使う" hint="四級海技士（航海）など" />
                <Toggle k="has_number" label="番号の欄を使う" />
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <Toggle k="notify_admin" label="陸上管理者に通知（アラート）" />
                <Toggle k="notify_self" label="本人に通知（連絡・お知らせ＋プッシュ）" />
                <Toggle k="visible_to_self" label="本人がプロフィールで見られる" hint="OFFにすると本人には期限も通知も出ない" />
                <Toggle k="visible_to_captain" label="船長（役職者）が自船の乗組員分を見られる" hint="健康診断など、船長にも見せない場合はOFF" />
                {editing.id && <Toggle k="is_active" label="有効（OFFで一覧から外す）" />}
              </div>
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#f8fafc' }}>
              <button className="btn-o" onClick={() => setEditing(null)} disabled={saving}>キャンセル</button>
              <button className="btn-p" onClick={saveType} disabled={saving || !(editing.name ?? '').trim()}>{saving ? '保存中…' : '保存'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CrewCertificatesPage;
